import { useEffect, useState } from 'react';
import { getArcaPlanReturns } from '../api/plan-returns/getPlanReturns';
import { PlanReturnView, toArcaPlanReturnsBySlug } from '../utils/transferReturns';
import { validYearsToRetirement } from '../utils/monthlyProjection';

export type ArcaPlanReturnsBySlug = Record<string, PlanReturnView | null>;

/**
 * Una petición por sesión y años hasta la jubilación: la pantalla de exploración, la lista de planes y el dashboard
 * leen el mismo dato (cambia poco: comparsa lo cachea 10 minutos). Solo se guardan las
 * respuestas buenas; un fallo se vuelve a pedir la próxima vez.
 */
const cache = new Map<string, Promise<ArcaPlanReturnsBySlug | null>>();

/** Solo para tests: vacía la caché entre casos. */
export function resetArcaPlanReturnsCache(): void {
	cache.clear();
}

function load(
	token: string,
	yearsToRetirement: number | undefined
): Promise<ArcaPlanReturnsBySlug | null> {
	const key = `${token}|${yearsToRetirement ?? ''}`;
	if (!cache.has(key)) {
		const promise = getArcaPlanReturns(token, yearsToRetirement)
			.then((res) => (res ? toArcaPlanReturnsBySlug(res) : null))
			// Respuesta mal formada: como un fallo.
			.catch(() => null)
			.then((bySlug) => {
				if (bySlug === null) cache.delete(key);
				return bySlug;
			});
		cache.set(key, promise);
	}
	return cache.get(key) as Promise<ArcaPlanReturnsBySlug | null>;
}

/**
 * Rentabilidad de cada plan de Arca por slug del catálogo, decidida por comparsa
 * (`/plan-returns/arca-plans`, plan nuevo = 5 años). `undefined` mientras llega («cargando»);
 * si falla, `{}`: todos «sin datos», nunca la tasa escrita en `ARCA_PLANS`.
 *
 * Con los años hasta la jubilación (`yearsToRetirementFor`), los planes de ciclo de vida llegan
 * con sus tramos (chirigota#592). Sin ellos, o fuera de rango, sin tramos.
 */
export default function useArcaPlanReturns(
	token: string,
	yearsToRetirementRaw?: number
): ArcaPlanReturnsBySlug | undefined {
	const [bySlug, setBySlug] = useState<ArcaPlanReturnsBySlug | undefined>(undefined);
	const yearsToRetirement = validYearsToRetirement(yearsToRetirementRaw);
	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		setBySlug(undefined);
		load(token, yearsToRetirement).then((res) => {
			if (!cancelled) setBySlug(res ?? {});
		});
		return () => {
			cancelled = true;
		};
	}, [token, yearsToRetirement]);
	return bySlug;
}
